<!--
  Screen 1 of the onboarding flow (registration redesign 2026-06-20):
  "welcome to Poolit → choose a club to request membership".

  This is the ENTRY a `needsRegistration` user (authed, no membership) lands on
  — the three guards (requireMembership / requireSupplier / redirectIfAuthenticated)
  and the App.vue boot-time pre-empt all route here. It is initial-URL-eligible
  (direct hard-load, or the /login/ → /join/ boot rewrite for an authed
  no-membership user), so its route uses EAGER `component:` (f7v9-vue3-playbook
  Section 5 row 16). Because this renders a real <f7-page>, F7 skips its beforeEnter on
  a cold hard-load (Section 5 row 22) — the member-bounce for that case is handled by
  the App.vue boot pre-empt, exactly like /login/.

  All member-facing copy is i18n keys only (onboarding.welcome.* / onboarding.club.*).
  P0-B authors the Arabic via poolit-voice; nothing is hardcoded here.
-->
<template>
  <f7-page name="club-choice" class="club-choice-page">
    <!-- Violet header: wordmark + headline + platform line -->
    <header class="cc-header">
      <img
        src="@/assets/poolit-logo.svg"
        alt="Poolit"
        class="cc-wordmark"
      />
      <h1 class="cc-headline">{{ $t('onboarding.welcome.headline') }}</h1>
      <p class="cc-platform">{{ $t('onboarding.welcome.platform') }}</p>
    </header>

    <div class="cc-body">
      <!-- "Rocket" promise strip -->
      <div class="cc-promise">
        <f7-icon f7="rocket_fill" class="cc-promise-icon"></f7-icon>
        <span class="cc-promise-text">{{ $t('onboarding.welcome.promise') }}</span>
      </div>

      <!-- «اختار ناديك» -->
      <h2 class="cc-choose">{{ $t('onboarding.welcome.chooseClub') }}</h2>

      <!-- Club cards -->
      <button
        type="button"
        class="cc-card cc-card--sellers"
        @click="chooseClub(CLUB_TYPES.SELLERS)"
      >
        <span class="cc-card-chip">
          <f7-icon f7="bag_fill"></f7-icon>
        </span>
        <span class="cc-card-text">
          <span class="cc-card-name">{{ $t('onboarding.club.sellers.name') }}</span>
          <span class="cc-card-desc">{{ $t('onboarding.club.sellers.desc') }}</span>
        </span>
        <f7-icon :f7="chevronIcon" class="cc-card-chevron"></f7-icon>
      </button>

      <button
        type="button"
        class="cc-card cc-card--suppliers"
        @click="openSupplierGains"
      >
        <span class="cc-card-chip">
          <f7-icon f7="cube_box_fill"></f7-icon>
        </span>
        <span class="cc-card-text">
          <span class="cc-card-name">{{ $t('onboarding.club.suppliers.name') }}</span>
          <span class="cc-card-desc">{{ $t('onboarding.club.suppliers.desc') }}</span>
          <!-- Signed-in only: the endpoint answers 401 to a visitor, and a 401 logs out. -->
          <supplier-headline-numbers
            v-if="isSignedIn"
            class="cc-card-numbers"
          ></supplier-headline-numbers>
        </span>
        <f7-icon :f7="chevronIcon" class="cc-card-chevron"></f7-icon>
      </button>

      <!-- Poola peek — the marketing shop window is open read-only to ANY
           signed-in user (prospect-open-reads 2026-07-10). Let a joining
           prospect preview the Sellers Club's Poola offers before committing to
           a club. Reuses the already-approved poola.pending.* teaser copy; taps
           through to the standalone /poola/ route (guard = requireAuth). -->
      <button
        type="button"
        class="cc-poola-peek"
        @click="peekPoola"
      >
        <f7-icon f7="flame_fill" class="cc-poola-peek-icon"></f7-icon>
        <span class="cc-poola-peek-body">
          <span class="cc-poola-peek-line">{{ $t('poola.pending.bannerLine') }}</span>
          <span class="cc-poola-peek-cta">{{ $t('poola.pending.bannerCta') }}</span>
        </span>
        <f7-icon :f7="chevronIcon" class="cc-poola-peek-chevron"></f7-icon>
      </button>

      <!-- Trust line -->
      <div class="cc-trust">
        <f7-icon f7="checkmark_seal_fill" class="cc-trust-icon"></f7-icon>
        <span>{{ $t('onboarding.welcome.free') }}</span>
      </div>

      <!-- Login footer -->
      <p class="cc-login">
        <span>{{ $t('onboarding.welcome.haveAccount') }}</span>
        <f7-link class="cc-login-link" @click="goLogin">
          {{ $t('onboarding.welcome.login') }}
        </f7-link>
      </p>
    </div>
  </f7-page>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed, onMounted } from 'vue';
import { CLUB_TYPES } from '@/clubs/manifest.js';
import SupplierHeadlineNumbers from '@/components/supplier/SupplierHeadlineNumbers.vue';
import { useLanguage } from '@/composables/useLanguage';
import { useAuthStore } from '@/stores/index.js';
import { poolaPathForStage } from '@/utils/poolaPaths.js';

// F7 injects `f7route`/`f7router` into routed page components (see WalletPage,
// SellerRegistrationForm). Prefer the injected router; fall back to the main
// view router for safety (mirrors SellerRegistrationForm.vue).
const props = defineProps({
  f7route: { type: Object, default: null },
  f7router: { type: Object, default: null },
});

const { isArabic } = useLanguage();
const isSignedIn = computed(() => useAuthStore().isAuthenticated);

// Forward affordance points toward the reading direction's end: left in RTL.
const chevronIcon = computed(() => (isArabic.value ? 'chevron_left' : 'chevron_right'));

function router() {
  // Null-safe fallback: f7.views.main can be momentarily null before the main
  // view's init() runs. Click handlers only fire post-init, but keep the guard
  // honest (matches App.vue's f7?.views?.main?.router pattern).
  return props.f7router ?? f7?.views?.main?.router;
}

/**
 * Carry the chosen club to the membership-request form via a query param
 * (?club=sellers|suppliers). Query is the SSOT for the chosen club during
 * onboarding — it survives reload/deep-link and is read by the form (P1-A)
 * as `props.f7route.query.club`. The onboarding flow is pre-membership and
 * pre-shell, so the route-derived useClubContext doesn't apply here.
 *
 * @param {string} clubType - CLUB_TYPES.SELLERS | CLUB_TYPES.SUPPLIERS
 */
function chooseClub(clubType) {
  router().navigate(`/join/form/?club=${clubType}`);
}

// The supplier card opens the gains page first (supplier-messages P2-A) — the
// application form is one tap further, from its bottom button.
function openSupplierGains() {
  router().navigate('/join/gains/');
}

function goLogin() {
  router().navigate('/login/');
}

// Preview the Poola shop window before joining. /poola/ is read-only to any
// signed-in user (a /join/ visitor is authed-but-membership-less → resolves to
// meta.viewerRole:'prospect'). A plain push so the back button returns here.
// Stage-aware like the waiting screen: anyone supplier-side opens the supplier
// twin (/suppliers/poola/), everyone else /poola/.
function peekPoola() {
  const authStore = useAuthStore();
  router().navigate(
    poolaPathForStage({ supplierStage: authStore.supplierStage, isSupplier: authStore.isSupplier }),
  );
}

onMounted(() => {
  // Mirror the other onboarding screens: assert the document direction on mount
  // in case this is the first screen painted on a cold boot.
  document.documentElement.setAttribute('dir', isArabic.value ? 'rtl' : 'ltr');
});
</script>

<style scoped>
.club-choice-page {
  /* Warm cream body (no design-system "cream" token exists; screen-local). */
  --cc-body-bg: #fbf8f4;
  --cc-card-bg: #ffffff;
  --cc-name-color: var(--poolit-text-headline);
  --cc-desc-color: var(--poolit-text-muted);
  background-color: var(--cc-body-bg);
}

/* Violet header — full-bleed, brand gradient, respects the status-bar inset. */
.cc-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-block: calc(max(env(safe-area-inset-top), 16px) + 24px) 32px;
  padding-inline: 24px;
  background: linear-gradient(
    135deg,
    var(--poolit-primary-900) 0%,
    var(--poolit-primary-600) 100%
  );
  color: var(--poolit-text-on-primary);
  border-end-start-radius: 28px;
  border-end-end-radius: 28px;
}

.cc-wordmark {
  width: 132px;
  height: auto;
  margin-bottom: 16px;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2));
}

.cc-headline {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
  color: var(--poolit-text-on-primary);
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}

.cc-platform {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  opacity: 0.92;
  max-width: 30ch;
}

.cc-body {
  padding-inline: 20px;
  padding-block: 24px calc(max(env(safe-area-inset-bottom), 12px) + 24px);
}

/* Light-violet promise strip carrying the "rocket" value line. */
.cc-promise {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: var(--poolit-radius-lg);
  background-color: var(--poolit-primary-50);
  color: var(--poolit-primary-700);
  margin-bottom: 28px;
}

.cc-promise-icon {
  flex-shrink: 0;
  font-size: 26px;
  color: var(--poolit-primary-600);
}

.cc-promise-text {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
}

.cc-choose {
  margin: 0 0 14px;
  font-size: 17px;
  font-weight: 700;
  color: var(--poolit-text-headline);
  text-align: start;
}

/* Tappable club card. */
.cc-card {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 16px;
  margin-bottom: 14px;
  border: 1px solid var(--poolit-border-color-light);
  border-radius: var(--poolit-radius-lg);
  background-color: var(--cc-card-bg);
  box-shadow: var(--poolit-shadow-soft);
  text-align: start;
  cursor: pointer;
  transition: transform var(--poolit-transition-fast) var(--poolit-transition-ease),
    box-shadow var(--poolit-transition-fast) var(--poolit-transition-ease);
  /* Card identity strip on the inline-start edge (RTL-safe). */
  border-inline-start: 4px solid var(--cc-accent);
}

.cc-card:active {
  transform: scale(0.985);
  box-shadow: var(--poolit-shadow-md);
}

.cc-card--sellers {
  --cc-accent: var(--poolit-primary-500);
  --cc-chip-bg: var(--poolit-primary-100);
  --cc-chip-fg: var(--poolit-primary-700);
}

.cc-card--suppliers {
  --cc-accent: var(--poolit-warning-500);
  --cc-chip-bg: var(--poolit-warning-100);
  --cc-chip-fg: var(--poolit-warning-700);
}

.cc-card-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: var(--poolit-radius-md);
  background-color: var(--cc-chip-bg);
  color: var(--cc-chip-fg);
}

.cc-card-chip .icon {
  font-size: 26px;
}

.cc-card-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
}

.cc-card-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--cc-name-color);
}

.cc-card-desc {
  font-size: 13px;
  line-height: 1.4;
  color: var(--cc-desc-color);
}

.cc-card-numbers {
  margin-top: 8px;
}

.cc-card-chevron {
  flex-shrink: 0;
  font-size: 20px;
  color: var(--poolit-text-placeholder);
}

/* Poola peek — secondary teaser strip (below the primary club cards). Softer
   than a club card so it never competes with the join CTAs. */
.cc-poola-peek {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-top: 4px;
  padding: 14px 16px;
  border: 1px solid var(--poolit-primary-100);
  border-radius: var(--poolit-radius-lg);
  background: linear-gradient(135deg, var(--poolit-primary-50), var(--poolit-accent-50));
  text-align: start;
  cursor: pointer;
  transition: transform var(--poolit-transition-fast) var(--poolit-transition-ease);
}

.cc-poola-peek:active {
  transform: scale(0.985);
}

.cc-poola-peek-icon {
  flex-shrink: 0;
  font-size: 22px;
  color: var(--poolit-primary-600);
}

.cc-poola-peek-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1 1 auto;
  min-width: 0;
}

.cc-poola-peek-line {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--poolit-text-headline);
}

.cc-poola-peek-cta {
  font-size: 13px;
  font-weight: 700;
  color: var(--poolit-primary-700);
}

.cc-poola-peek-chevron {
  flex-shrink: 0;
  font-size: 18px;
  color: var(--poolit-primary-400);
}

.cc-trust {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 18px;
  font-size: 14px;
  color: var(--poolit-text-muted);
}

.cc-trust-icon {
  font-size: 18px;
  color: var(--poolit-success-600);
}

.cc-login {
  margin: 16px 0 0;
  text-align: center;
  font-size: 14px;
  color: var(--poolit-text-secondary);
}

.cc-login-link {
  display: inline;
  margin-inline-start: 6px;
  font-weight: 700;
  color: var(--poolit-text-link);
}
</style>

<!--
  Non-scoped block: dark-mode overrides. Vue's :global() compound form is
  forbidden inside <style scoped> (CLAUDE.md) — F7 darkMode:'auto' adds `.dark`
  on <html>, so these targeted, cc-prefixed rules live in a plain block.
-->
<style>
.dark .club-choice-page {
  --cc-body-bg: var(--poolit-bg-secondary-dark);
  --cc-card-bg: var(--poolit-bg-tertiary-dark);
  --cc-name-color: var(--poolit-text-primary-dark);
  --cc-desc-color: var(--poolit-text-muted-dark);
}

.dark .club-choice-page .cc-promise {
  background-color: var(--poolit-primary-900);
  color: var(--poolit-primary-200);
}

.dark .club-choice-page .cc-card {
  border-color: var(--poolit-border-color-on-dark);
}

.dark .club-choice-page .cc-poola-peek {
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--poolit-primary-500) 24%, transparent),
    color-mix(in srgb, var(--poolit-accent-500) 14%, transparent)
  );
  border-color: var(--poolit-border-color-on-dark);
}

.dark .club-choice-page .cc-poola-peek-line {
  color: #fff;
}

.dark .club-choice-page .cc-poola-peek-cta {
  color: var(--poolit-primary-200);
}

.dark .club-choice-page .cc-poola-peek-icon {
  color: var(--poolit-primary-300);
}
</style>
